import { ref, computed, watch, onMounted, onUnmounted, type MaybeRef } from 'vue';
import { usePositionUserSelection } from '@/entities/User';
import { formatDateForApi, useCustomToast, type UserItem } from '@/shared/lib';

import { notificationApi, type CreateNotificationPayload } from '../api/notification.api';

export type PeriodicityMode = 'DAILY' | 'WEEKLY' | 'BIWEEKLY' | 'MONTHLY' | 'CUSTOM_DAYS' | 'CUSTOM_DATES';

export function useNotificationForm(
  emit: (event: string, ...args: unknown[]) => void,
  options?: { onAfterReset?: () => void; extraMembers?: MaybeRef<UserItem[]> },
) {
  const toast = useCustomToast();
  const {
    positions,
    filteredUsers: users,
    selectedPositions,
    selectedUsers,
    fetchPositionsAndUsers,
    initializeSelections,
    resetSelection,
  } = usePositionUserSelection({
      mode: 'by-selected-positions',
      fetchMembers: async () => {
        const members = await notificationApi.getRecipients();
        return members.map((m) => ({
          id: m.id,
          name: m.name,
          email: m.email,
          positions: (m.positions ?? []).map((p) => ({
            id: p.id,
            name: p.name,
          })),
          status: 'ACTIVE',
          deletedAt: null,
        }));
      },
      extraMembers: options?.extraMembers,
    });

  const title = ref('');
  const description = ref('');
  const periodicityMode = ref<PeriodicityMode>('DAILY');
  const customDays = ref(1);
  const startDate = ref<Date | null>(null);
  const endDate = ref<Date | null>(null);
  const customDates = ref<(Date | null)[]>([null]);
  const isSaving = ref(false);

  const startDatePickerOpen = ref(false);
  const endDatePickerOpen = ref(false);
  const customDatePickerOpen = ref<boolean[]>([false]);
  const customDateKeys = ref<number[]>([1]);
  let nextKeyCounter = 1;

  const buildToday = () => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  };

  const today = ref(buildToday());

  let midnightTimer: ReturnType<typeof setTimeout> | null = null;

  const scheduleMidnightUpdate = () => {
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    midnightTimer = setTimeout(() => {
      today.value = buildToday();
      scheduleMidnightUpdate();
    }, tomorrow.getTime() - now.getTime());
  };

  const minEndDate = computed(() => {
    if (!startDate.value) return today.value;
    const d = new Date(startDate.value);
    d.setDate(d.getDate() + 1);
    d.setHours(0, 0, 0, 0);
    return d;
  });

  watch(startDate, (newStart) => {
    if (!newStart || !endDate.value) return;
    const end = new Date(endDate.value);
    end.setHours(0, 0, 0, 0);
    if (end < minEndDate.value) {
      endDate.value = null;
    }
  });

  const isFormValid = computed(() => {
    if (!title.value.trim() || !description.value.trim()) return false;
    if (selectedUsers.value.length === 0 || selectedPositions.value.length === 0) return false;

    if (periodicityMode.value === 'CUSTOM_DAYS') {
      return customDays.value >= 1 && startDate.value !== null && endDate.value !== null;
    }

    if (periodicityMode.value === 'CUSTOM_DATES') {
      return customDates.value.length > 0 && customDates.value.every((d) => d !== null);
    }

    return startDate.value !== null && endDate.value !== null;
  });

  const buildPayload = (isDraft: boolean): CreateNotificationPayload => {
    const selectedUsersIds = selectedUsers.value.map((id) => Number(id));
    const normalizedStartDate = startDate.value
      ? (formatDateForApi(startDate.value) ?? undefined)
      : undefined;
    const normalizedEndDate = endDate.value
      ? (formatDateForApi(endDate.value) ?? undefined)
      : undefined;

    if (periodicityMode.value === 'CUSTOM_DATES') {
      const sortedDates = customDates.value.filter((d): d is Date => d !== null);
      return {
        title: title.value,
        description: description.value,
        periodicity: 'CUSTOM',
        ...(sortedDates.length > 0 && { customDates: sortedDates.map((d) => formatDateForApi(d)!) }),
        selectedUsers: selectedUsersIds,
        isDraft,
      };
    }

    if (periodicityMode.value === 'CUSTOM_DAYS') {
      return {
        title: title.value,
        description: description.value,
        periodicity: 'CUSTOM',
        customDays: customDays.value,
        startDate: normalizedStartDate,
        endDate: normalizedEndDate,
        selectedUsers: selectedUsersIds,
        isDraft,
      };
    }

    return {
      title: title.value,
      description: description.value,
      periodicity: periodicityMode.value,
      startDate: normalizedStartDate,
      endDate: normalizedEndDate,
      selectedUsers: selectedUsersIds,
      isDraft,
    };
  };

  const generateNextKey = () => ++nextKeyCounter;

  const resetForm = () => {
    title.value = '';
    description.value = '';
    periodicityMode.value = 'DAILY';
    customDays.value = 1;
    startDate.value = null;
    endDate.value = null;
    customDates.value = [null];
    selectedPositions.value = [];
    selectedUsers.value = [];
    startDatePickerOpen.value = false;
    endDatePickerOpen.value = false;
    customDatePickerOpen.value = [false];
    customDateKeys.value = [generateNextKey()];
    options?.onAfterReset?.();
  };

  const handleClose = () => {
    resetForm();
    emit('update:isOpen', false);
  };

  const setCustomDate = (index: number, date: Date | null) => {
    customDates.value[index] = date;

    const paired = customDates.value.map((d, i) => ({
      date: d,
      open: customDatePickerOpen.value[i] ?? false,
      key: customDateKeys.value[i] ?? i,
    }));

    const filled = paired.filter(
      (p): p is { date: Date; open: boolean; key: number } => p.date !== null,
    );
    const nullSlots = paired.filter((p) => p.date === null);

    filled.sort((a, b) => a.date.getTime() - b.date.getTime());

    const sorted = [...filled, ...nullSlots];
    customDates.value = sorted.map((p) => p.date);
    customDatePickerOpen.value = sorted.map((p) => p.open);
    customDateKeys.value = sorted.map((p) => p.key);
  };

  const openCustomDatePicker = (index: number) => {
    customDatePickerOpen.value[index] = true;
  };

  const setCustomDatePickerOpen = (index: number, value: boolean) => {
    customDatePickerOpen.value[index] = value;
  };

  const addCustomDate = () => {
    customDates.value.push(null);
    customDateKeys.value.push(generateNextKey());
    customDatePickerOpen.value.push(false);
  };

  const removeCustomDate = (index: number) => {
    if (customDates.value.length > 1) {
      customDates.value.splice(index, 1);
      customDatePickerOpen.value.splice(index, 1);
      customDateKeys.value.splice(index, 1);
    }
  };

  onMounted(() => scheduleMidnightUpdate());

  onUnmounted(() => {
    if (midnightTimer !== null) clearTimeout(midnightTimer);
  });

  return {
    toast,
    positions,
    users,
    selectedPositions,
    selectedUsers,
    fetchPositionsAndUsers,
    initializeSelections,
    resetSelection,
    generateNextKey,
    title,
    description,
    periodicityMode,
    customDays,
    startDate,
    endDate,
    customDates,
    customDateKeys,
    isSaving,
    isFormValid,
    today,
    minEndDate,
    startDatePickerOpen,
    endDatePickerOpen,
    customDatePickerOpen,
    buildPayload,
    resetForm,
    handleClose,
    setCustomDate,
    openCustomDatePicker,
    setCustomDatePickerOpen,
    addCustomDate,
    removeCustomDate,
  };
}
